import * as z from 'zod/v4';

import { BiProductFootprintArtifactKind } from '@watershed/constants/productFootprint';

const tableReferenceTypeSchema = z.enum([
  'CTE',
  'FootprintSnapshot',
  'FinanceFootprintSnapshot',
  'FootprintBart',
  'FootprintSidecar',
  'Bart',
  'BartInstance',
  'Forex',
  'SyncedPostgres',
  'DataRegistry',
  'LineageAssetFileMetadata',
  'ActivityDatasetVersion',
  'ReferenceData',
  'ProductFootprint',
]);
/** Table reference type */
export type TableReferenceType = z.infer<typeof tableReferenceTypeSchema>;
// eslint-disable-next-line @typescript-eslint/no-redeclare
export const TableReferenceType: { [T in TableReferenceType]: T } =
  tableReferenceTypeSchema.enum;

const unaliasedBaseTableReferenceSchema = z.object({
  ref: z.string() /** @deprecated - being removed */,
  alias: z.string().optional(),
});

const aliasSchema = { alias: z.string() };

const baseTableReferenceSchema =
  unaliasedBaseTableReferenceSchema.extend(aliasSchema);

export type BaseTableReference = z.infer<typeof baseTableReferenceSchema>;

const cteTableReferenceSchema = unaliasedBaseTableReferenceSchema.extend({
  tableType: z.literal(TableReferenceType.CTE),
  ref: z.string(),
});
export type CteTableReference = z.infer<typeof cteTableReferenceSchema>;

/** An array of footprint snapshot ids used to query multiple footprints */
const optionalSnapshotIdsSchema = z.array(z.string()).optional().nullable();

const footprintSnapshotTableReferenceSchema = baseTableReferenceSchema.extend({
  // If provided, the footprintSnapshotIds will be used instead of the ref to identify the footprint snapshot(s)
  footprintSnapshotIds: optionalSnapshotIdsSchema,
  tableType: z.literal(TableReferenceType.FootprintSnapshot),
  // Note that the GQL types don't allow these to be undefined, but we will accept them if they are
  includeDedupedRows: z.boolean().optional().nullable(),
  footprintKind: z.string().optional().nullable(),
});
export type FootprintSnapshotTableReference = z.infer<
  typeof footprintSnapshotTableReferenceSchema
>;

const financeFootprintSnapshotTableReferenceSchema =
  baseTableReferenceSchema.extend({
    tableType: z.literal(TableReferenceType.FinanceFootprintSnapshot),
    footprintSnapshotIds: optionalSnapshotIdsSchema,
  });
export type FinanceFootprintSnapshotTableReference = z.infer<
  typeof financeFootprintSnapshotTableReferenceSchema
>;

const footprintBartTableReferenceSchema = baseTableReferenceSchema.extend({
  // If provided, the footprintSnapshotIds will be used instead of the ref to identify the footprint snapshot(s)
  footprintSnapshotIds: optionalSnapshotIdsSchema,
  tableType: z.literal(TableReferenceType.FootprintBart),
  businessActivityType: z.string(),
});

export type FootprintBartTableReference = z.infer<
  typeof footprintBartTableReferenceSchema
>;

const activityDatasetReference = z.object({
  activityDatasetId: z.string().nonempty(),
  activityDatasetVersionId: z.string().nonempty().nullable(),
});

const bartTableReferenceSchema = baseTableReferenceSchema.extend({
  tableType: z.literal(TableReferenceType.Bart),
  businessActivityType: z.string().optional(),
  datasourceIds: z.tuple([z.string()], z.string()).nullish(),
  measurementProjectIds: z.tuple([z.string()], z.string()).nullish(),
  activityDatasets: z.array(activityDatasetReference).nullish(),
  consolidateExpenditureTypes: z.boolean().optional().nullable(),
});

export type BartTableReference = z.infer<typeof bartTableReferenceSchema>;

const bartInstanceTableReferenceSchema = baseTableReferenceSchema.extend({
  tableType: z.literal(TableReferenceType.BartInstance),
  bartIds: z.tuple([z.string()], z.string()),
  areStableIds: z.boolean().optional().nullable(),
  areExtBartStableIds: z.boolean().optional().nullable(),
});

export type BartInstanceTableReference = z.infer<
  typeof bartInstanceTableReferenceSchema
>;

const activityDatasetVersionTableReferenceSchema =
  baseTableReferenceSchema.extend({
    tableType: z.literal(TableReferenceType.ActivityDatasetVersion),
    activityDatasetVersionId: z.string().nonempty(),
  });

export type ActivityDatasetVersionTableReference = z.infer<
  typeof activityDatasetVersionTableReferenceSchema
>;

const forexTableReferenceSchema = baseTableReferenceSchema.extend({
  tableType: z.literal(TableReferenceType.Forex),
  slug: z.string().optional(),
});

export type ForexTableReference = z.infer<typeof forexTableReferenceSchema>;

const syncedPostgresTableReferenceSchema = baseTableReferenceSchema.extend({
  tableType: z.literal(TableReferenceType.SyncedPostgres),
  tableName: z.string().optional(),
});

const productFootprintTableReferenceSchema = baseTableReferenceSchema.extend({
  tableType: z.literal(TableReferenceType.ProductFootprint),
  // For now, this'll be the data registry table name,
  // but eventually this needs to handle the partitioning of the file
  tableName: z.string(),
  kind: z.nativeEnum(BiProductFootprintArtifactKind),
});

export type ProductFootprintTableReference = z.infer<
  typeof productFootprintTableReferenceSchema
>;

export type SyncedPostgresTableReference = z.infer<
  typeof syncedPostgresTableReferenceSchema
>;

export const sidecarTableNameSchema = z.enum([
  'emissions_factor',
  'emissions_model_emissions_factor',
  'intensity_denominator',
  'eme_trace_addendum',
]);

/** Enum of footprint sidecar file types */
export type SidecarTableName = z.infer<typeof sidecarTableNameSchema>;
// eslint-disable-next-line @typescript-eslint/no-redeclare
export const SidecarTableName: { [T in SidecarTableName]: T } =
  sidecarTableNameSchema.enum;

const footprintSidecarTableReferenceSchema = baseTableReferenceSchema.extend({
  // If provided, the footprintSnapshotIds will be used instead of the ref to identify the footprint snapshot(s)
  footprintSnapshotIds: optionalSnapshotIdsSchema,
  tableType: z.literal(TableReferenceType.FootprintSidecar),
  tableName: sidecarTableNameSchema,
});

export type FootprintSidecarTableReference = z.infer<
  typeof footprintSidecarTableReferenceSchema
>;

const permissionReferenceTypeSchema = z.enum(['FootprintSnapshot', 'ExtBart']);

/** Enum of permission reference types */
export type PermissionReferenceType = z.infer<
  typeof permissionReferenceTypeSchema
>;
// eslint-disable-next-line @typescript-eslint/no-redeclare
export const PermissionReferenceType: { [T in PermissionReferenceType]: T } =
  permissionReferenceTypeSchema.enum;

// This may eventually be expanded to include other types of permission references,
// in which case this becomes a union of those types.
const footprintSnapshotPermissionReferenceSchema = z.object({
  type: z.literal(PermissionReferenceType.FootprintSnapshot),
  footprintSnapshotId: z.string(),
});

const extBartPermissionReferenceSchema = z.object({
  type: z.literal(PermissionReferenceType.ExtBart),
  extBartIds: z.tuple([z.string()], z.string()),
});

export const permissionReferenceSchema = z.union([
  footprintSnapshotPermissionReferenceSchema,
  extBartPermissionReferenceSchema,
]);

export type PermissionReference =
  | z.infer<typeof footprintSnapshotPermissionReferenceSchema>
  | z.infer<typeof extBartPermissionReferenceSchema>;

const dataRegistryTableReferenceSchema = baseTableReferenceSchema.extend({
  tableType: z.literal(TableReferenceType.DataRegistry),
  tableName: z.string(),
  permissionReference: permissionReferenceSchema,
});

export type DataRegistryTableReference = z.infer<
  typeof dataRegistryTableReferenceSchema
>;

// The ref id for this schema is a FileMetadata id
const lineageAssetFileMetadataTableReferenceSchema =
  baseTableReferenceSchema.extend({
    tableType: z.literal(TableReferenceType.LineageAssetFileMetadata),
    fmdId: z.string(),
    permissionReference: permissionReferenceSchema,
  });

export type LineageAssetFileMetadataTableReference = z.infer<
  typeof lineageAssetFileMetadataTableReferenceSchema
>;

// This uses a `referenceDataVersionId` (instead of a revisionId) because
// Calculation Customization allows exactly 1 revision per version. Even when
// we're displaying WS-owned reference data, we'll resolve the latest published
// global revision for a given version.
const referenceDataTableReferenceSchema = baseTableReferenceSchema.extend({
  tableType: z.literal(TableReferenceType.ReferenceData),
  referenceDataVersionId: z.string(),
});

export type ReferenceDataTableReference = z.infer<
  typeof referenceDataTableReferenceSchema
>;

// This refers to external-facing tables that can be referenced by a user
// when constructing a query. Notably it excludes the Forex table which is
// only used when resolving the ConvertCurrency operator
export const externalConcreteTableReferenceSchema = z.union([
  activityDatasetVersionTableReferenceSchema,
  footprintSnapshotTableReferenceSchema,
  financeFootprintSnapshotTableReferenceSchema,
  footprintBartTableReferenceSchema,
  bartTableReferenceSchema,
  bartInstanceTableReferenceSchema,
  syncedPostgresTableReferenceSchema,
  footprintSidecarTableReferenceSchema,
  dataRegistryTableReferenceSchema,
  lineageAssetFileMetadataTableReferenceSchema,
  referenceDataTableReferenceSchema,
  productFootprintTableReferenceSchema,
]);

export type ExternalConcreteTableReference = z.infer<
  typeof externalConcreteTableReferenceSchema
>;

const concreteTableReferenceSchema = z.union([
  externalConcreteTableReferenceSchema,
  forexTableReferenceSchema,
]);

/** An object describing an identifier to a concrete table */
export type ConcreteTableReference = z.infer<
  typeof concreteTableReferenceSchema
>;

/** An object describing a reference to a specific table */
export const tableReferenceSchema = z.union([
  cteTableReferenceSchema,
  concreteTableReferenceSchema,
]);

/** An object describing a reference to a specific table */
export type TableReference = z.infer<typeof tableReferenceSchema>;

/** A table reference with a guaranteed alias */
export const aliasedTableReferenceSchema = z.union([
  cteTableReferenceSchema.extend(aliasSchema),
  concreteTableReferenceSchema,
]);

/** A table reference with a guaranteed alias */
export type AliasedTableReference = z.infer<typeof aliasedTableReferenceSchema>;
